// =============================================================================
// FILE: packages/renderer/src/components/FeatureBadge.tsx
// PURPOSE: Small visual marker rendered next to nav items and headings
//          when a feature is not in the ACTIVE state.
//
//          ACTIVE       → no badge
//          DISABLED     → muted "off" pill
//          LOCKED       → 🔒 indigo pill
//          EXPERIMENTAL → cyan "exp" pill (non-default styling per spec)
// =============================================================================

import { Lock } from 'lucide-react';
import type { FeatureState } from '../hooks/useFeatureRegistry.js';

export function FeatureBadge({ state }: { state: FeatureState }): JSX.Element | null {
  if (state === 'ACTIVE') return null;

  const cls =
    state === 'EXPERIMENTAL' ? 'border-cyan-500/50 bg-cyan-500/10 text-cyan-300' :
    state === 'LOCKED'       ? 'border-indigo-500/50 bg-indigo-500/10 text-indigo-300' :
                                'border-zinc-700 bg-zinc-900 text-zinc-500';

  const label =
    state === 'EXPERIMENTAL' ? 'exp' :
    state === 'LOCKED'       ? null  :
                                'off';

  return (
    <span
      data-testid={`feature-badge-${state.toLowerCase()}`}
      title={state}
      className={`ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] uppercase tracking-[0.18em] rounded-sm border ${cls}`}
    >
      {state === 'LOCKED' && <Lock className="h-2.5 w-2.5" strokeWidth={2.5} />}
      {label && <span>{label}</span>}
    </span>
  );
}
